/**
 * Gatsby Browser API configuration file
 * Wraps the root element with MDXProvider to enable MDX content rendering throughout the site
 */
import "./src/styles/globals.css";
import * as React from "react";
import { MDXProvider } from "@mdx-js/react";

const mdxComponents = {
  h1: () => null,
};

export const wrapRootElement = ({ element }) => {
  return <MDXProvider components={mdxComponents}>{element}</MDXProvider>;
};

/** Same property as gatsby-plugin-google-gtag in gatsby-config.mjs. */
const GTAG_SRC = "https://www.googletagmanager.com/gtag/js?id=G-MT0FG103KN";

/**
 * Load gtag.js after the page has loaded and the browser is idle, so analytics
 * never competes with first paint (gatsby-ssr.js removes the plugin's early
 * <script>). Page views fired before then wait in `dataLayer` and are sent
 * when it arrives. `gtag` is undefined when the visitor sends Do Not Track
 * (the plugin respects it), and then nothing loads.
 */
export const onInitialClientRender = () => {
  if (
    process.env.NODE_ENV !== "production" ||
    typeof window.gtag !== "function"
  ) {
    return;
  }
  const inject = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = GTAG_SRC;
    document.head.appendChild(script);
  };
  const whenIdle = () =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(inject, { timeout: 3000 })
      : window.setTimeout(inject, 1500);
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
};

const normalizePath = (pathname) =>
  (pathname || "/").replace(/\/$/, "") || "/";

/**
 * Scroll to top when navigating to a new page. Without this, Gatsby can keep
 * the previous scroll position (e.g. from deep on the homepage), so links like
 * "View All FAQs" land at the bottom of the destination page.
 */
export const shouldUpdateScroll = ({ routerProps, prevRouterProps }) => {
  const { pathname, hash } = routerProps.location;
  const prevPathname = prevRouterProps?.location?.pathname;

  if (pathname !== prevPathname) {
    return [0, 0];
  }
  if (hash) {
    return false;
  }
  return true;
};

/**
 * Gatsby’s scroll + hash handling can still leave the viewport wrong after SPA
 * navigation. Re-assert scroll-to-top when entering /faq from another route.
 */
export const onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window === "undefined") return;

  const path = normalizePath(location.pathname);
  const prevPath = prevLocation ? normalizePath(prevLocation.pathname) : null;

  if (path !== "/faq" || prevPath === path) return;

  const scrollTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  scrollTop();
  requestAnimationFrame(() => {
    scrollTop();
    requestAnimationFrame(scrollTop);
  });
  window.setTimeout(scrollTop, 0);
  window.setTimeout(scrollTop, 50);
};
